import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CRUDActionEnum, AuthoritiesEnum, RoutesEnum, IntegrationTypeEnum, EventSurveyStatusEnum, EventResponseStatusEnum } from "~/types/enums";
import { Modal, Spinner, Text, Button, Card, Grid, Picture, Icon, InputToggle, Badge, Avatar, Link } from "~/components-shared";
import eventsActionCreators from "~/store/events/actions";
import eventSelectors from "~/store/events/selectors";
import { useHistory } from "react-router-dom";
import moment from "moment";
import lists from "~/lib/lists";
import { resolveDates } from "~/lib/helpers";
import toastsActionCreators from "~/store/toasts/actions";
import integrationSelectors from "~/store/integrations/selectors";
import useAbacExt from "~/hooks/abac";
import EventResponseCard from "~/components/EventResponseCard";
import styled, { useTheme } from "styled-components";
import { composeCalendarURIFromEvent } from "~/integrations/google/helpers";
import Markdown from "~/components/Markdown";
const Status = styled.i`
  position: absolute;
  top: 8px;
  left: 8px;
  border-radius: 50%;
  background: ${_ref => {
  let {
    status,
    theme
  } = _ref;
  switch (status) {
    case EventResponseStatusEnum.ACCEPTED:
      return theme.colors.success.main;
    case EventResponseStatusEnum.DECLINED:
      return theme.colors.danger.main;
    default:
      return theme.colors.borders.secondary;
  }
}};
  width: 16px;
  height: 16px;
  border: 2px solid ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.primary;
}};
  color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.background.primary;
}};
  font-style: initial;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const EventReadModal = () => {
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const event = useSelector(eventSelectors.getSelected);
  const eventId = event?.id || "";
  const googleIntegration = useSelector(integrationSelectors.getMemberIntegrationByType(IntegrationTypeEnum.GOOGLE));
  const history = useHistory();
  const actions = [];
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const handleCancel = () => {
    dispatch(eventsActionCreators.actionChanged(undefined, undefined));
  };
  const handleAddToCalendar = () => {
    if (event) {
      window.open(composeCalendarURIFromEvent(event), "_blank");
    }
  };
  const handleJoinVideoCall = () => {
    if (event?.videoLink) {
      window.open(event.videoLink, "_blank");
    }
  };
  const handleCopyLink = () => {
    const type = "text/plain";
    const blob = new Blob([`${window.location.origin}${RoutesEnum.EVENT.replace(":eventId", eventId || "")}`], {
      type
    });
    const data = [new ClipboardItem({
      [type]: blob
    })];
    navigator.clipboard.write(data).then(() => {
      dispatch(toastsActionCreators.pushed({
        message: t("EventReadModal.copiedToClipboard"),
        timeout: 3000
      }));
    });
  };
  const handleCommunityClick = id => {
    history.push(RoutesEnum.COMMUNITY.replace(":communityId", id));
  };
  const handleViewMember = id => {
    history.push(RoutesEnum.MEMBER.replace(":memberId", id));
  };
  const handleViewSurvey = id => {
    history.push(RoutesEnum.EVENT_SURVEY.replace(":eventSurveyId", id));
  };
  const handleView = () => {
    handleCancel();
    history.push(RoutesEnum.EVENT.replace(":eventId", eventId));
  };
  const handleUpdate = () => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.UPDATE, eventId));
  };
  const handleDelete = () => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.DELETE, eventId));
  };
  actions.push({
    icon: "share",
    tip: t("EventReadModal.copyEventLink"),
    onClick: handleCopyLink
  });
  actions.push({
    icon: "eye",
    tip: t("common.actions.view"),
    onClick: handleView
  });
  if (userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event?.createdBy.id)) {
    actions.push({
      icon: "pencil",
      tip: t("common.actions.edit"),
      onClick: handleUpdate
    });
  }
  if (userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event?.createdBy.id)) {
    actions.push({
      icon: "bin",
      tip: t("common.actions.delete"),
      onClick: handleDelete
    });
  }
  return /*#__PURE__*/React.createElement(Modal.Body, {
    title: event?.name,
    onClose: handleCancel,
    actions: actions,
    maxWidth: "600px",
    fullWidth: true
  }, event ? /*#__PURE__*/React.createElement(Modal.Content, null, /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    color: "textSecondary",
    weight: "semiBold",
    s: {
      mb: 5
    },
    display: "inline-block"
  }, resolveDates(event.dateFrom, event.dateTo)), /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    equal: true
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("EventForm.type")), /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 5
    },
    display: "inline-block"
  }, /*#__PURE__*/React.createElement(Badge, {
    color: lists.events.types.colors(theme)[event.type]
  }, lists.events.types.names[event.type]))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("EventReadModal.communities")), event.communities.length ? event.communities.map(community => /*#__PURE__*/React.createElement(Link, {
    color: theme.colors.primary.main,
    onClick: () => handleCommunityClick(community.id),
    display: "block",
    s: {
      mb: 1
    },
    underline: true
  }, community.name)) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary"
  }, t("EventReadModal.allCommunities")))), /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("EventForm.description")), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    s: {
      mb: 5
    }
  }, event.description ? /*#__PURE__*/React.createElement(Markdown, {
    text: event.description
  }) : "-"), /*#__PURE__*/React.createElement(EventResponseCard, {
    s: {
      mb: 5,
      px: 5
    },
    eventId: event.id
  }), /*#__PURE__*/React.createElement(Grid, {
    cols: 2
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("EventReadModal.attendees")), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "people",
    color: theme.colors.text.secondary,
    scale: 3
  }), " ", event.attendees.length, " ", /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: theme.colors.secondary.main
  }, "(", event.attendees.filter(attendee => attendee.active).length, ")")))), event.attendees.length ? /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    equal: true,
    s: {
      mb: 5
    },
    gap: 3,
    align: "stretch"
  }, event.attendees.map(attendee => /*#__PURE__*/React.createElement(Card, {
    key: attendee.id,
    onClick: () => handleViewMember(attendee.id),
    s: {
      p: 3
    },
    border: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: "2.6rem 1fr 20px",
    gap: 3,
    align: "center"
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Avatar, {
    user: attendee,
    size: 9
  }), attendee.responseStatus !== EventResponseStatusEnum.NEEDS_ACTION && /*#__PURE__*/React.createElement(Status, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("Event.accepted"),
    status: attendee.responseStatus
  }, attendee.responseStatus === EventResponseStatusEnum.TENTATIVE ? "?" : /*#__PURE__*/React.createElement(Icon, {
    src: attendee.responseStatus === EventResponseStatusEnum.ACCEPTED ? "tick" : "cross",
    scale: 3
  }))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    color: "textSecondary",
    display: "block",
    fontSize: 3,
    lineHeight: 3.5
  }, attendee.role.name), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block",
    fontSize: 4
  }, attendee.firstName, " ", attendee.lastName), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    display: "block",
    lineHeight: 4
  }, attendee.organizer && `${t("Event.organizer")} · `, attendee.email)), /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    rotate: 90,
    color: theme.colors.secondary.main
  }))))) : /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 7
    },
    display: "inline-block",
    color: "textSecondary"
  }, t("EventReadModal.noAttendees")), /*#__PURE__*/React.createElement(Grid, {
    cols: 2
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h5",
    s: {
      mb: 3
    }
  }, t("EventReadModal.surveys")), /*#__PURE__*/React.createElement(Grid.Cell, {
    justify: "end"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    color: "textSecondary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "document",
    color: theme.colors.text.secondary,
    scale: 3.5
  }), " ", event.eventSurveys.length))), event.eventSurveys.length ? /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    equal: true,
    s: {
      mb: 5
    },
    gap: 3
  }, event.eventSurveys.map(survey => /*#__PURE__*/React.createElement(Card, {
    key: survey.id,
    onClick: () => handleViewSurvey(survey.id),
    s: {
      p: 3
    },
    border: true
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    template: "2.6rem 1fr 20px",
    gap: 3,
    align: "center"
  }, /*#__PURE__*/React.createElement(Picture, {
    name: "S",
    width: "2.6rem",
    height: "2.6rem",
    border: "circled"
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    color: "textSecondary",
    display: "block",
    fontSize: 3,
    lineHeight: 3.5
  }, t("Event.sendVia", {
    target: lists.eventSurveys.target.names[survey.target]
  })), /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    display: "block",
    fontSize: 4
  }, t("Event.questions", {
    count: survey.questions.length
  })), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    display: "block",
    lineHeight: 3.5
  }, survey.status === EventSurveyStatusEnum.SENT ? t("Event.sent") : survey.scheduledAt ? t("Event.scheduledAt", {
    time: moment(survey.scheduledAt).format("ddd, MMM D hh:mma")
  }) : t("Event.notScheduled"))), /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    rotate: 90,
    color: theme.colors.secondary.main
  }))))) : /*#__PURE__*/React.createElement(Text, {
    s: {
      mb: 7
    },
    display: "inline-block",
    color: "textSecondary"
  }, t("EventReadModal.noSurveys")), slackIntegration && /*#__PURE__*/React.createElement(InputToggle, {
    label: t("EventReadModal.shareSlack"),
    value: event.shareInSlack,
    s: {
      mb: 2
    },
    disabled: true
  }), googleIntegration && /*#__PURE__*/React.createElement(InputToggle, {
    label: t("EventReadModal.googleCalendarSync"),
    value: event.googleCalendarSync,
    s: {
      mb: 2
    },
    disabled: true
  })) : /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: handleCancel,
    rounded: true
  }, t("common.actions.close")), /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: handleAddToCalendar,
    rounded: true
  }, t("EventReadModal.addToCalendar")), event?.videoLink && /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: handleJoinVideoCall,
    s: {
      ml: 2,
      px: 8
    },
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "videocam",
      s: {
        mr: 2
      }
    }),
    rounded: true
  }, t("EventReadModal.joinVideoCall"))));
};
export default EventReadModal;